import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { urlHashes } from '../../../utils/helpers/constants';
import { ItemPhotosGalleryProps } from './types';
import { CurrentItemPhotosContainer } from './styled';
import { GallerySlider } from '../../ui/GallerySlider';
import { SliderInfo } from './SliderInfo';
import useScreenSize from '../../../hooks/useScreenSize';
import { useFullScreenGallery } from './useFullScreenGallery';
import { usePageNumberOnScroll } from './usePageNumberOnScroll';
import { GalleryCarousel } from '../../ui/GalleryCarousel';
import { GalleryFooter } from './GalleryFooter';
import { GalleryHeader } from './GalleryHeader';
import { useClient } from '../../../hooks/useClient';
import { Breakpoints } from '../../../styled/constants';
import { TargetedBannerIds } from '../../../types';

const FullScreenGallery = dynamic(
  () => import('../../ui/FullScreenGallery').then(mod => mod.FullScreenGallery),
  { ssr: false }
);

export const ItemPhotosGallery: React.FC<ItemPhotosGalleryProps> = ({
  photos = [],
  price = '',
  description = '',
  vipped = false,
  featured = false,
  isBookmarked,
  setFavorited,
  isShowBookmark,
  setClickOnGallery,
  isLeased,
  isAuthor,
  isExpiredManually,
  title,
  itemId,
  isReported,
  phones,
  isExpired,
  maskedPhones,
  setIsAdReported,
  adriverCustom,
  setIsVisible,
  isVisible
}) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { isSmall, isLarge } = useScreenSize();
  const isClient = useClient();

  const openGallery = useFullScreenGallery();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const {
    slidesToRender,
    pageNumber,
    scrollToNextPhoto,
    scrollToPrevPhoto,
    scrollToPhoto,
    isBannerPosition,
    isBannerActivated,
    shouldRenderBanner,
    setShouldRenderBanner,
    isNextBanner
  } = usePageNumberOnScroll(photos, scrollRef, 1, isLarge);

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [initialGalleryIndex, setInitialGalleryPhoto] = useState<number | null>(
    null
  );

  const onOpenGallery = (
    e: React.SyntheticEvent<HTMLDivElement>,
    photoNumber = pageNumber
  ) => {
    setInitialGalleryPhoto(photoNumber);
    e.stopPropagation();
    openGallery();
  };

  useEffect(() => {
    setIsGalleryOpen(router.asPath.includes(urlHashes.fullScreenGallery));
  }, [router]);

  if (!photos?.length) {
    return null;
  }

  const handleGalleryClose = () => {
    setIsGalleryOpen(false);

    const isCameFromGrid = sessionStorage.getItem('cameFromGrid');

    if (isCameFromGrid) {
      router.back();
    } else {
      router.replace(router.asPath.split('#')[0], undefined, {
        shallow: true,
        scroll: false
      });
    }
  };

  const overlayText = t(
    `pages.current_item.status_message.expired_${isLeased ? 'lease' : 'sell'}`
  );

  const isShowPhoneButton = !(isExpired && !isAuthor);
  const phonesToRender = isAuthor ? maskedPhones : phones;

  return (
    <CurrentItemPhotosContainer>
      <GallerySlider
        photos={slidesToRender}
        description={description}
        scrollRef={scrollRef}
        scrollToPrevPhoto={scrollToPrevPhoto}
        scrollToNextPhoto={scrollToNextPhoto}
        onOpenGallery={onOpenGallery}
        isNextBanner={isNextBanner}
        setShouldRenderBanner={setShouldRenderBanner}
        shouldRenderBanner={shouldRenderBanner}
        isBannerPosition={isBannerPosition}
        sliderInfo={
          <SliderInfo
            isVippedShown={vipped}
            isFeaturedShown={featured}
            photos={photos}
            pageNumber={pageNumber}
            description={title}
            isAuthor={isAuthor}
            isPublished={isShowBookmark}
            itemId={itemId}
            isReported={isReported}
            isFavorited={isBookmarked}
            setFavorited={setFavorited}
            onOpenGallery={onOpenGallery}
            setIsAdReported={setIsAdReported}
          />
        }
        shouldRenderButton={!isExpiredManually}
        shouldRenderOverlay={!!isExpiredManually}
        overlayText={overlayText}
        isBannerActivated={isBannerActivated}
        bannerId={TargetedBannerIds.gallery}
        adriverCustom={adriverCustom}
      />

      {(!isClient || (!isSmall && isClient)) && (
        <GalleryCarousel
          photos={photos}
          pageNumber={pageNumber}
          onOpenGallery={onOpenGallery}
          scrollToPhoto={scrollToPhoto}
          photoLimit={8}
          hideFromWidth={`${Breakpoints.tablet}px`}
          description={description}
        />
      )}

      {isGalleryOpen && initialGalleryIndex && (
        <FullScreenGallery
          description={description}
          isOpen={isGalleryOpen}
          photos={photos}
          initialGalleryIndex={initialGalleryIndex}
          adriverCustom={adriverCustom}
          footer={
            <GalleryFooter
              price={price}
              description={description}
              setClickOnGallery={setClickOnGallery}
              isAuthor={isAuthor}
              phones={phonesToRender}
              isShowPhones={isShowPhoneButton}
            />
          }
          onClose={handleGalleryClose}
          renderHeader={(currentPage, showInfo) => (
            <GalleryHeader
              itemId={itemId}
              pageNumber={currentPage}
              photosLength={photos?.length}
              isFavorited={isBookmarked}
              price={price}
              description={description}
              isAuthor={isAuthor}
              phones={phonesToRender}
              onClose={handleGalleryClose}
              setClickOnGallery={setClickOnGallery}
              setFavorited={setFavorited}
              isShowBookmark={isShowBookmark}
              isShowPhones={isShowPhoneButton}
              shouldHideSliderInfo={showInfo}
              setIsVisible={setIsVisible}
              isVisible={isVisible}
            />
          )}
        />
      )}
    </CurrentItemPhotosContainer>
  );
};
